/**
 * StatementViewer — shows one pay statement exactly as it is emailed (Oct 19,
 * 2026). The commission-paid-email edge function renders it dry (nothing is
 * sent or written); a team member may render their own statements, finance
 * anyone's. The HTML goes in a sandboxed iframe: no scripts, no navigation.
 */
import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { invokeErrorMessage } from "../../lib/edgeError";

export default function StatementViewer({ title, body, onClose }: { title: string; body: Record<string, unknown>; onClose: () => void }) {
  const [html, setHtml] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const key = JSON.stringify(body);

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setErr(null);
    (async () => {
      const { data, error } = await supabase.functions.invoke("commission-paid-email", { body: JSON.parse(key) });
      if (cancelled) return;
      if (error) { setErr(await invokeErrorMessage(error, "Couldn't open the statement")); return; }
      const d = data as { html?: string; reason?: string } | null;
      if (d?.html) setHtml(d.html);
      else setErr(d?.reason ?? "Nothing to show");
    })();
    return () => { cancelled = true; };
  }, [key]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-2 sm:p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={title} className="my-4 w-full max-w-2xl rounded-xl bg-white shadow-xl sm:my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-bardin-ink">{title}</h3>
          <button onClick={onClose} className="text-xl leading-none text-slate-400 hover:text-slate-700" aria-label="Close">×</button>
        </div>
        {err ? (
          <div className="p-4 text-sm text-red-700">{err}</div>
        ) : html === null ? (
          <div className="p-6 text-sm text-slate-500">Opening the statement…</div>
        ) : (
          <iframe title={title} sandbox="" srcDoc={html} className="h-[75vh] w-full rounded-b-xl" />
        )}
      </div>
    </div>
  );
}
